import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Typography,
  Avatar,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
  Room as RoomIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  WorkOutline as WorkIcon,
} from "@mui/icons-material";
import CandidateSkills from "./CandidateSkills";
import CandidateExperience from "./CandidateExperience";
import CandidateEducation from "./CandidateEducation";
import CandidatePersonalInfo from "./CandidatePersonalInfo";
import sharedStyles from "./sharedStyles";
import { _formatDate } from "../../../Utils/AppDateUtils";
import { getMockCandidateData } from "../../../api/agent";
import { useMutation } from "@tanstack/react-query";
interface CandidateData {
  name: string;
  title: string;
  location: string;
  availability: string;
  email: string;
  phone: string;
  countryCode: string;
  image: string;
  skills: string[];
  currentStatus: string;
  workExperience: string;
  yearOfExperience: string;
  availabilityStatus:string;
  readyToRelocate: string;
  address: string;
  workAuthorization: string;
  education: {
    degree: string;
    university: string;
    graduation: string;
  }[];
  certifications: {
    name: string;
    year: string;
  }[];
  experience: {
    employer: string;
    title: string;
    location: string;
    startDate: string;
    endDate: string;
    summary: string;
    responsibilities: string[];
    highlights: string[];
  }[];
  resumeLink: any;
}

interface SkillHeadingProps {
  jobTitle?: string;
  jobStatus?: string;
  postingDate?: string;
}
interface CandidateDetailProps {
  eximiusId: string;
  candidatesData: any;
  // jobId: string;
  onClose: () => void;
}
const CandidateDetail: React.FC<CandidateDetailProps> = ({
  eximiusId,
  candidatesData,
  onClose,
}) => {
  const [candidateData, setCandidateData] = useState<CandidateData | null>(
    null
  );
  const [recentCandidates] = useState<any[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    if (recentCandidates.length > 0 && eximiusId) {
      const index = recentCandidates.findIndex(
        (candidate) => candidate.eximiusId === eximiusId
      );
      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
  }, [recentCandidates, eximiusId]);

  useEffect(() => {
    if (!candidatesData) return;   
    let resumeRecord;
  
    if (candidatesData.messageList && Array.isArray(candidatesData.messageList)) {
      // Extract from API response structure
      resumeRecord = candidatesData.messageList[0]?.resumeRecord;
    } else if (candidatesData.resumeRecord) {
      // Directly use `resumeRecord` if available
      resumeRecord = candidatesData.resumeRecord;
    }
  
    if (resumeRecord) {
      const parsedData = resumeRecord.parsedUserInfo;
      const additionalParams = resumeRecord.additionalParameters;
      const resumeLink = resumeRecord.file?.source || "";
  
      if (parsedData) {
        const skillsData = parsedData.Skills?.categories || [];
        const allSkills = extractSkills(skillsData);
  
        const experienceData = extractWorkExperience(parsedData.Work);
  
        const candidateDetailsData = buildCandidateDetails(
          parsedData,
          additionalParams,
          allSkills,
          experienceData,
          resumeLink
        );
  
        setCandidateData(candidateDetailsData);
      }
    }
  }, [candidatesData]);
  

  const extractSkills = (skillsData: any[]): string[] => {
    const allSkills: string[] = [];
    skillsData.forEach((category: { levels: any[] }) => {
      category.levels.forEach((level: any) => {
        if (Array.isArray(level.skills)) {
          allSkills.push(...level.skills);
        }
      });
    });
    return Array.from(new Set(allSkills)); // Deduplicate skills
  };
  const formatPhoneNumber = (phoneNumber: number | string): string => {
    const phoneString = phoneNumber.toString().replace(/\D/g, ""); // Remove non-numeric characters
    const phoneRegex = /(\d{3})(\d{3})(\d{4})/;

    const matches = phoneString.match(phoneRegex);

    if (matches) {
      return `(${matches[1]})-${matches[2]}-${matches[3]}`;
    }

    return phoneString; // Return as is if it doesn't match the expected format
  };

  const extractWorkExperience = (workData: any[]): any[] => {
    const experience =
      workData?.map((work: any) => ({
        employer: work.employer || "N/A",
        title: work.title || "N/A",
        location: work.location || "N/A",
        startDate: work.startDate ? _formatDate(work.startDate) : "N/A",
        endDate:
          work.endDate === "Present" ? "Present" : _formatDate(work.endDate),
        summary: work.summary || "N/A",
        responsibilities: work.responsibilities?.responsibility || [],
        highlights: work.highlights?.highlight || [],
      })) || [];

    return experience.sort(
      (a, b) =>
        new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );
  };

  const buildCandidateDetails = (
    parsedData: any,
    additionalParams: any,
    uniqueSkills: string[],
    experienceData: any[],
    resumeLink: string
  ): CandidateData => {
    const getValue = (value: any, defaultValue = "N/A") =>
      value || defaultValue;

    const formatPhones = (phones: any[]) =>
      phones?.map(formatPhoneNumber).join(", ") || "N/A";
    const latestExperienceTitle =
      experienceData.length > 0 ? experienceData[0].title : "N/A";
    const formattedPhoneNumbers = formatPhones(parsedData?.Personal?.phone);

    return {
      name: `${getValue(parsedData.Personal?.firstName)} ${getValue(
        parsedData.Personal?.lastName
      )}`,
      title: latestExperienceTitle,
      location: getValue(additionalParams?.location),
      availability: getValue(additionalParams?.availabilityStatus),
      availabilityStatus: getValue(additionalParams?.availabilityStatus),
      email: getValue(parsedData.Personal?.email),
      phone: formattedPhoneNumbers,
      countryCode: getValue(parsedData.Personal?.location?.countryCode, "+1"),
      image: getValue(parsedData.image, ""),
      skills: uniqueSkills,
      currentStatus: "Actively looking for a job",
      workExperience: parsedData.Personal?.yearsOfExperience
        ? `${parsedData.Personal.yearsOfExperience} Years`
        : "N/A",
      readyToRelocate:
        additionalParams?.willingToRelocate === "y" ? "Yes" : "No",
      address: getValue(additionalParams?.location),
      workAuthorization: getValue(additionalParams?.jobWorkAuthorization),
      education: buildEducation(parsedData.Education),
      certifications: buildCertifications(parsedData.Certifications),
      experience: experienceData,
      resumeLink,
      yearOfExperience: parsedData.Personal?.yearsOfExperience,
    };
  };


  const formatDate = (date: string) => {
    if (!date) return ''; // If no date, return empty string
    const parts = date.split('-'); // Split into [year, month, day]
    const year = parts[0];
    return year; // Only return the year
  };

  const buildEducation = (educationData: any[]): any[] => {
    return (
      educationData?.map(
        (edu: {
          studyType: any;
          institution: any;
          endDate: any;
          area: any;
        }) => {
          let degree = "";
          if (edu.studyType && edu.area) {
            degree =
              edu.studyType === edu.area
                ? edu.studyType
                : `${edu.studyType}-${edu.area}`;
          } else {
            degree = edu.studyType || edu.area || "";
          }
          
  
          return {
            degree,
            university: edu.institution,
            graduation: formatDate(edu.endDate),
          };
        }
      ) || []
    );
  };
  

  const buildCertifications = (certificationsData: any[]): any[] => {
    return (
      certificationsData?.map((cert: { name: any; date: any }) => {
  
        return {
          name: cert.name,
          year: formatDate(cert.date), // Show only the year
        };
      }) || []
    );
  };
  

  // const handleNavigation = (direction: "prev" | "next") => {
  //   const newIndex = direction === "prev" ? currentIndex - 1 : currentIndex + 1;

  //   if (newIndex >= 0 && newIndex < recentCandidates.length) {
  //     const newCandidate = recentCandidates[newIndex];
  //     setCurrentIndex(newIndex);
  //   }
  // };

  if (!candidateData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
       
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, borderTop: "1px solid #e5e7eb",paddingBottom:"120px" }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        {/* Navigation and Close Buttons */}
        <Stack
          direction="row"
          justifyContent="end"
          alignItems="center"
          mb={2}
        >
          {/* <IconButton
            onClick={() => handleNavigation("prev")}
            disabled={currentIndex === 0}
            sx={{
              ...sharedStyles.navButton,
              opacity: currentIndex === 0 ? 0.5 : 1,
            }}
            data-testid="Arrow-Back"
          >
            <ArrowBackIcon />
          </IconButton>

          <IconButton
            onClick={() => handleNavigation("next")}
            disabled={currentIndex === recentCandidates.length - 1}
            sx={{
              ...sharedStyles.navButton,
              opacity: currentIndex === recentCandidates.length - 1 ? 0.5 : 1,
            }}
            data-testid="Arrow-Forward"
          >
            <ArrowForwardIcon />
          </IconButton> */}

          <IconButton onClick={onClose} sx={sharedStyles.navButton}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Candidate Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "center", sm: "flex-start" },
            gap: 2,
            backgroundColor: "white",
            p: 2,
            borderRadius: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <Avatar
            src={candidateData.image}
            sx={{ width: 80, height: 80 }}
            data-testid="candidate-avatar"
          />
          <Box sx={{ flex: 1, width: { xs: "100%", sm: "auto" } }}>
            <Box sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 2, 
              mb: 1,
              justifyContent: { xs: "center", sm: "flex-start" }
            }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "#1e293b" }}
                data-testid="candidate-name"
              >
                {candidateData.name}
              </Typography>
            </Box>
            <Box sx={{ 
              display: "flex", 
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" }, 
              gap: { xs: 1, sm: 3 }, 
              mb: 1 
            }}>
              <Typography
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "#475569",
                  fontSize: "0.875rem",
                }}
              >
                <RoomIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
                {candidateData.location}
              </Typography>
              <Typography
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "#475569",
                  fontSize: "0.875rem",
                }}
              >
                <WorkIcon sx={{ fontSize: "1.1rem", mr: 0.5 }} />
                {candidateData.yearOfExperience} Years
              </Typography>
            </Box>
            <Box sx={{ 
              display: "flex", 
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" }, 
              gap: { xs: 1, sm: 3 }
            }}>
              <Typography
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "#475569",
                  fontSize: "0.875rem",
                  wordBreak: "break-word"
                }}
              >
                <EmailIcon sx={{ fontSize: "1rem", mr: 0.5, flexShrink: 0 }} />
                {candidateData.email}
              </Typography>
              <Typography
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "#475569",
                  fontSize: "0.875rem",
                }}
              >
                <PhoneIcon sx={{ fontSize: "1rem", mr: 0.5, flexShrink: 0 }} />
                {candidateData.phone}
              </Typography>
            </Box>
            {candidateData.title && (
              <Box sx={{ 
                mt: 1,
                display: "flex",
                justifyContent: { xs: "center", sm: "flex-start" }
              }}>
                <Typography
                  sx={{
                    display: "inline-block",
                    backgroundColor: "#EEF4FE",
                    color: "#3b82f6",
                    px: 2,
                    py: 0.5,
                    borderRadius: 2,
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                  data-testid="candidate-title"
                >
                  {candidateData.title}
                </Typography>
              </Box>
            )}
          </Box>
</Box>
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            <CandidatePersonalInfo
              data={{
                status: candidateData.currentStatus,
                experience: candidateData.workExperience,
                relocation: candidateData.readyToRelocate,
                address: candidateData.address,
                workAuth: candidateData.workAuthorization,
                name: candidateData.name,
                email: candidateData.email,
                phone: candidateData.phone,
                location: candidateData.location,
                yearOfExperience: candidateData.yearOfExperience,
                availability: candidateData.availabilityStatus,
              }}
            />

            <CandidateEducation
              education={candidateData.education}
              certifications={candidateData.certifications}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} md={6}>
          <CandidateExperience experience={candidateData.experience} />
        </Grid>

        <Grid item xs={12}>
          <CandidateSkills candidateData={candidateData} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CandidateDetail;
